/**
 * Hero da home da plataforma. Puramente apresentacional.
 *
 * É o LCP da página pública: a imagem de fundo é opcional e, quando ausente,
 * o trio de gradientes assume — assim a dobra pinta sem depender de download.
 *
 * Empilhamento (de trás para frente), como no mockup do designer:
 *   .mv-hero-bg → imagem opcional → sinapses → .mv-hero-scrim → conteúdo
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIcon, PlayIcon } from './icons';

interface CourseHeroProps {
  backgroundImage?: string;
  onExplore: () => void;
  onMoreInfo?: () => void;
}

/**
 * Rede neural decorativa do canto direito.
 *
 * Fica aqui, e não em icons.tsx, porque é arte de uma tela só. Ancorada à
 * direita (`xMaxYMid`): os nós vivem entre x=800 e x=1120 de um viewBox de 1200,
 * então centralizar cortaria justamente o desenho em telas estreitas.
 */
const HeroSynapse: React.FC = () => (
  <svg
    className="absolute inset-0 -z-20 h-full w-full opacity-50 mix-blend-screen"
    viewBox="0 0 1200 600"
    preserveAspectRatio="xMaxYMid slice"
    aria-hidden="true"
  >
    <g stroke="#B217C3" strokeWidth="1.1" fill="none" opacity=".55">
      <path d="M980 90 L1080 170 L1010 280 L1120 340" />
      <path d="M1080 170 L960 250 L1010 280" />
      <path d="M960 250 L890 160 L980 90" />
      <path d="M1010 280 L920 400 L1040 470" />
      <path d="M920 400 L800 340 L890 160" />
    </g>
    <g fill="#fff">
      <circle cx="980" cy="90" r="4" />
      <circle cx="1080" cy="170" r="5" />
      <circle cx="960" cy="250" r="4" />
      <circle cx="1010" cy="280" r="6" fill="#B217C3" />
      <circle cx="890" cy="160" r="3.5" />
      <circle cx="920" cy="400" r="4.5" />
      <circle cx="800" cy="340" r="3.5" />
      <circle cx="1120" cy="340" r="4" />
      <circle cx="1040" cy="470" r="4" />
    </g>
  </svg>
);

const btnBase =
  'inline-flex items-center gap-2.5 rounded-[11px] px-[22px] py-[13px] text-sm font-bold transition-[transform,box-shadow,background-color] duration-200 ease-mv active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-accent-hi focus-visible:ring-offset-2 focus-visible:ring-offset-mv-bg';

const CourseHero: React.FC<CourseHeroProps> = ({
  backgroundImage,
  onExplore,
  onMoreInfo,
}) => {
  const { t } = useTranslation();

  return (
    <section className="relative isolate flex min-h-[min(56dvh,460px)] items-end overflow-hidden p-[clamp(28px,5vw,64px)] nav:min-h-[min(62vh,560px)]">
      <div className="mv-hero-bg absolute inset-0 -z-20" />

      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          // Acima da dobra: nunca lazy, e prioridade alta de download.
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-right"
        />
      )}

      <HeroSynapse />

      <div className="mv-hero-scrim absolute inset-0 -z-10" />

      <div className="max-w-[720px]">
        <p className="mb-[18px] flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.26em] text-mv-accent after:h-px after:w-[46px] after:bg-mv-accent after:opacity-55 after:content-['']">
          {t('platform.hero.eyebrow')}
        </p>

        {/* O espaço antes do destaque é inquebrável, como os &nbsp; do mockup:
            "da sua mão." fica na mesma linha em qualquer largura. */}
        <h1 className="font-display text-[clamp(34px,5.4vw,64px)] font-black leading-[1.02] tracking-[-0.015em] text-balance text-mv-text [text-shadow:0_4px_40px_rgba(10,16,40,.6)]">
          {t('platform.hero.lead')}
          {'\u00A0'}
          <span className="text-mv-accent">{t('platform.hero.accent')}</span>.
        </h1>

        <div className="mt-[30px] flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onExplore}
            className={`${btnBase} bg-mv-accent text-white shadow-[0_8px_30px_-8px_rgba(178,23,195,.7)] hover:bg-mv-accent-hi hover:shadow-[0_12px_40px_-8px_rgba(178,23,195,.85)]`}
          >
            <PlayIcon className="h-4 w-4" />
            {t('platform.hero.explore')}
          </button>

          {onMoreInfo && (
            <button
              type="button"
              onClick={onMoreInfo}
              className={`${btnBase} border border-white/10 bg-white/10 text-mv-text backdrop-blur-sm hover:bg-white/[0.16]`}
            >
              <InfoIcon className="h-4 w-4" />
              {t('platform.hero.moreInfo')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CourseHero;
